import React, { JSX, useMemo, useState } from 'react'
import { EmptyDataCard } from 'components/EmptyDataCard'
import { formatPrice } from 'lib/helpers'
import { TotalsBase } from 'types/api'
import { Tooltip } from 'react-tooltip'

export interface LeaderboardProps {
  data: TotalsBase[]
  defaultSortKey: string
  sortDirection?: 'asc' | 'desc'
  columns: string[]
  title: string
  limit?: number
  idField: string
  hoverData: boolean
  noDataMessage?: string
  hoverColumns?: HoverColumn[]
}

interface HoverColumn {
  colName: string
  displayName: string
}

const sortData = (
  data: TotalsBase[],
  sortKey: string,
  direction: 'asc' | 'desc'
): TotalsBase[] => {
  return [...data].sort((a, b) => {
    const aValue = a[sortKey]
    const bValue = b[sortKey]

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue
    }
    return 0
  })
}

const renderCell = (
  item: TotalsBase,
  col: string,
  isExpanded: boolean,
  isFirstCol: boolean
): JSX.Element => {
  const content = col === 'sales' ? formatPrice(item[col]) : item[col]
  const baseClasses = 'px-4 py-3 text-sm dark:text-gray-200'
  const alignmentClass = isFirstCol ? 'max-w-0 truncate overflow-hidden text-ellipsis' : 'whitespace-nowrap text-right'
  const innerClass = isExpanded ? 'whitespace-normal' : 'overflow-hidden text-ellipsis'

  return (
    <td key={col} className={`${baseClasses} ${alignmentClass}`}>
      <div className={innerClass}>{content}</div>
    </td>
  )
}

export const Leaderboard = ({
  data,
  defaultSortKey,
  sortDirection = 'desc',
  columns,
  title,
  idField,
  hoverData,
  hoverColumns,
  noDataMessage = 'No data available for specified filters',
  limit = 5
}: LeaderboardProps): JSX.Element => {
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  const sortedData = useMemo(() => {
    const sliced = sortData(data, defaultSortKey, sortDirection)
    return limit !== null ? sliced.slice(0, limit) : sliced
  }, [data, defaultSortKey, sortDirection, limit])

  if (data.length === 0) {
    return <EmptyDataCard message={noDataMessage} />
  }

  return (
    <div className='w-full'>
      <div className='flex items-center justify-between px-4 pt-2 pb-3'>
        <h2 className='text-sm font-medium'>{title}</h2>
      </div>

      <div className='overflow-x-auto'>
        <table className='w-full table-auto text-left whitespace-nowrap'>
          <colgroup>
            <col className='w-full' />
            <col className='w-auto' />
          </colgroup>

          <tbody>
            {sortedData.map((item) => {
              const isExpanded = expandedRow === item[idField]
              const toggleExpand = (): void => setExpandedRow(isExpanded ? null : item[idField])
              let tooltipHtml = ''
              if (hoverColumns != null && (hoverColumns.length > 0)) {
                tooltipHtml = `
                <div style="max-width:400px;">
                  ${hoverColumns
                    .map(
                      (col: HoverColumn) => `
                        <div style="margin-bottom:4px; font-size: 11px;">
                          <strong>${col.displayName}:</strong>
                          ${String(item[col.colName]) ?? '—'}
                        </div>
                      `).join('')}
                    </div>`
              }
              return (
                <tr
                  key={item[idField]}
                  className='even:bg-lm-bg-secondary dark:even:bg-dm-bg-secondary cursor-pointer'
                  onClick={toggleExpand}
                  data-tooltip-id='leaderboard-tooltip'
                  data-tooltip-html={tooltipHtml}
                >
                  {columns.map((col, index) =>
                    renderCell(item, col, isExpanded, index === 0)
                  )}

                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      {hoverData && <Tooltip id='leaderboard-tooltip' />}
    </div>
  )
}
